import { Transform, plainToInstance, Expose, ClassConstructor, Exclude } from 'class-transformer';
import { doc, DocumentReference, DocumentSnapshot, Firestore, Timestamp } from 'firebase/firestore';
import { logger } from '@/utils/logger'

function normalizePath(path: string | undefined | null): string {
    if (!path) return ''
    return path.replace(/^\/+/, '')
}

@Exclude()
export class DocRef {
    @Expose()
    public id!: string
    @Expose()
    public path!: string

    toDocumentReference(firestore: Firestore): DocumentReference | null {
        return this.path ? doc(firestore, this.path) : null
    }
}

/**
 * Transformer that converts a native Firestore DocumentReference to a plain DocRef.
 * class-transformer can't properly deserialize Firebase's native DocumentReference,
 * so we extract id/path into a simple class it can handle.
 */
export function toDocRef(x: { key?: string | number, obj?: any }): DocRef | null {
    if (x.key === undefined || x.obj === undefined) return null
    const value = x.obj[x.key]
    if (value && value instanceof DocumentReference) {
        try {
            const ref = new DocRef()
            // remove leading slash from path to match path format in models
            ref.path = normalizePath(value.path)
            ref.id = value.id
            return ref

        } catch (e) {
            logger.error('fromFirebase toDocRef ', x.key, 'malformed ref', value, e)
        }
    }
    return null
}

/**
 * Transformer for arrays of DocumentReferences → DocRef[].
 */
export function toDocRefArray(x: { key?: string, obj?: any }): DocRef[] {
    if (x.key === undefined || x.obj === undefined) return []
    const value = x.obj[x.key]
    if (!Array.isArray(value)) return []
    return value
        .map((_, index, array) => toDocRef({ key: index, obj: array }))
        .filter((v): v is DocRef => v !== null)
}

export function forceArray(x: { value: unknown }) {
    return Array.isArray(x.value) && typeof x.value !== "string" ? x.value : [x.value];
}

export function anyToDate(x: { obj: any, key: string }): Date | undefined {
    const v = x.obj[x.key]
    try {
        if (!v && v !== 0) return undefined
        if (v instanceof Timestamp) {
            const d = v.toDate()
            return Number.isNaN(d.getTime()) ? undefined : d
        }
        if (v instanceof Date) {
            return Number.isNaN(v.getTime()) ? undefined : v
        }
        if (typeof v === 'object' && typeof v.toDate === 'function') {
            const d = v.toDate()
            return d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined
        }
        if (typeof v === 'object' && typeof v.seconds === 'number') {
            const d = new Date(v.seconds * 1000)
            return isNaN(d.getTime()) ? undefined : d
        }
        if (typeof v === 'number') {
            const ms = v > 1e12 ? v : v * 1000
            const d = new Date(ms)
            return isNaN(d.getTime()) ? undefined : d
        }
        if (typeof v === 'string') {
            const d = new Date(v)
            return isNaN(d.getTime()) ? undefined : d
        }
        logger.debug('undefined', v)
        return undefined
    } catch (_) {
        return undefined
    }
}

export function forceMap<T>(cls: ClassConstructor<T>) {
    return (x: { value: any, obj?: any, key?: string }) => {
        const raw = x.obj && x.key ? x.obj[x.key] : x.value
        if (raw instanceof Object)
            return new Map(Object.entries(raw).map(([k, v]) => [k, plainToInstance(cls, v)]));
        return new Map();
    }
}

/**
 * @Transform helper for `Record<string, T>` fields.
 * Instantiates each value through `plainToInstance(cls, v)` and returns a plain object
 * (unlike `forceMap` which returns a `Map`).
 */
export function forceRecord<T>(cls: ClassConstructor<T>) {
    return (x: { value: any, obj?: any, key?: string }): Record<string, T> | undefined => {
        const raw = x.obj && x.key ? x.obj[x.key] : x.value
        if (!raw || typeof raw !== 'object') return undefined
        const out: Record<string, T> = {}
        for (const [k, v] of Object.entries(raw)) {
            out[k] = plainToInstance(cls, v, { excludeExtraneousValues: true })
        }
        return out
    }
}

@Exclude()
export abstract class _FirebaseDocument {
    get id(): string {
        return this.doc?.id;
    }
    get path(): string {
        return normalizePath(this.doc?.ref?.path);
    }
    @Expose()
    @Transform(anyToDate)
    public created_at: Date;
    @Expose()
    @Transform(anyToDate)
    public updated_at: Date;

    // removed date formatting getters; use vue-i18n $d in components instead

    get parent_path(): string {
        return normalizePath(this.path.slice(0, - `/${this.collection()}/${this.id}`.length))
    }

    @Exclude()
    public doc: DocumentSnapshot;

    toDocumentReference(): DocumentReference | null {
        return this.doc?.ref || null;
    }

    static _fromFirebase<T extends _FirebaseDocument>(cls: ClassConstructor<T>, o: DocumentSnapshot): T | null {
        if (!o.exists())
            return null;
        const data = o.data()
        const d = plainToInstance(cls, data, { excludeExtraneousValues: true })
        d.doc = o;
        return d;
    }

    abstract collection(): string;
}
